import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router";
import { useTranslation } from 'react-i18next';
import { scrollTop } from "../../../helpers/scrolling";
import {Modal, ModalHeader, ModalBody, ModalFooter} from "../../../components/Modal";
import CustomButton from "../../../components/CustomButton";
import RadioButton from "../../../components/RadioButton";
import Loading from "../../../components/Loading";
import BarValue from "../../../components/Bars/BarValue";
import iconLeft from "url:shared-assets/icons/white/arrow-left.svg"
import iconRight from "url:shared-assets/icons/black/arrow-right.svg"
import iconCompass from "url:shared-assets/icons/white/compass.svg";
import iconWarning from "url:shared-assets/icons/warning.svg";
import ProgressBar from "../../../components/Progress/ProgressBar";
import {questions_en, questions_sv, choices_en, choices_sv, agreeableness_en, conscientiousness_en, extraversion_en,
  neuroticism_en, openness_en, agreeableness_sv, conscientiousness_sv, extraversion_sv,
  neuroticism_sv, openness_sv} from "./helpers";
import {getLanguage} from "../../../lang/helpers";
import Api from "../../../services/Api";
import { todayStr } from "../../../helpers/dateFunctions";
import LocalBreadcrumbs from "../../../components/Breadcrumbs/LocalBreadcrumbs";


interface Results<T> {
  [key: string]: T;
}
interface Question {
  id: string
  text: string
  keyed: string
  domain: string
  facet: number
}

interface Answer {
  score: number
}

interface ClientInterface {
  id: string;
  firstName: string;
  lastName: string;
}

export default function BigFive() {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<Answer[]>([]);
  const { t, i18n } = useTranslation();
  const lang = getLanguage(i18n.language)
  const questions = lang === 'sv' ? questions_sv : questions_en;
  const choices = lang === 'sv' ? choices_sv : choices_en;
  const agreeableness = lang === 'sv' ? agreeableness_sv : agreeableness_en;
  const conscientiousness = lang === 'sv' ? conscientiousness_sv : conscientiousness_en;
  const extraversion = lang === 'sv' ? extraversion_sv : extraversion_en;
  const neuroticism = lang === 'sv' ? neuroticism_sv : neuroticism_en;
  const openness = lang === 'sv' ? openness_sv : openness_en;
  const [loaded, setLoaded] = useState<boolean>(false);
  const [pageNr, setPageNr] = useState(1);
  const [sending, setSending] = useState(false);
  const [calculatingResult, setCalculatingResult] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [changed, setChanged] = useState(false);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [created, setCreated] = useState(new Date());
  const [confirmRetake, setConfirmRetake] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState("-");
  const pages = Math.ceil(questions.length / 3);
  const [client, setClient] = useState<ClientInterface>();
  const domains = ['A', 'C', 'E', 'N', 'O'];
  const { personalityId } = useParams();

  useEffect(() => {
    const url = personalityId ? "coach/big5/" + personalityId : "client/profile/big5";
    api.get(url).then((response) => {
      const storedAnswers: Array<Answer> | null = response.json.answers
      if (storedAnswers) {
        setAnswers(storedAnswers);
        setAnalysis(response.json.analysis);
        setClient(response.json.client);
        let answered = 0
        for (const a of storedAnswers) {
          if (a.score === 0) {
            break;
          }
          answered++;
        }
        if (response.json.updated) {
          setCreated(new Date(response.json.updated));
        }
        let _pageNr = Math.ceil(answered / 3) + 1;
        if (_pageNr > 40) {
          _pageNr = 40;
        }
        setPageNr(_pageNr)
        setShowResult(answered === questions.length)
      } else {
        const _answers: Array<Answer> = [];
        for (const q of questions) {
          _answers.push({score: 0})
        }
        setAnswers(_answers)
      }
      setLoaded(true)
    });
  }, []);

  const pageDone = () => {
    let answered = 0
    for (const a of answers) {
      if (a.score === 0) {
        break;
      }
      answered++;
    }
    return Math.floor(answered / 3) < pageNr;

  }

  const setPage = (step: number) => {
    if (step === 1) {
      if (changed) {
        setSending(true);
        const lastAnswers = pageNr > (pages - 1)
        let _result: Results<any> | null = null;
        if (lastAnswers) {
          _result = calculateResults()
          setCalculatingResult(true);
        }
        const data = {
          answers: answers,
          result: _result
        }
        api.post("client/profile/big5", data).then((response) => {
          setSending(false);
          setCalculatingResult(false);
          if (lastAnswers) {
            setShowResult(true);
          } else {
            setPageNr(pageNr + step)
          }
          if (response.json.analysis) setAnalysis(response.json.analysis);
          scrollTop();
        });
      } else {
        if (pageNr > (pages - 1)) {
          setShowResult(true);
        } else {
          setPageNr(pageNr + step)
        }
        scrollTop();
      }
      setChanged(false);
    } else {
      if (pageNr === 1) {
        navigate("/profile")
      } else {
        setPageNr(pageNr + step);
        scrollTop();
      }
    }

  }

  const selectOption = (questionNr: number, score: number) => {
    const _answers = [...answers];
    _answers[questionNr].score = score;
    setAnswers(_answers);
    setChanged(true);
  }

  if (!loaded) {
    return <Loading />
  }

  if (calculatingResult) {
    return <Loading txt={lang === "sv" ? "Räknar ut din personlighetstyp..." : "Calculating your personality type..."}/>;
  }

  const getTemplate = (domain: string) => {
    if (domain === 'A') {
      return agreeableness
    } else if (domain === 'C') {
      return conscientiousness
    } else if (domain === 'N') {
      return neuroticism
    } else if (domain === 'E') {
      return extraversion
    } else {
      return openness
    }
  }

  const calculateScoreClass = (score: number) => {
    if (score > 3.5) {
      return lang === "sv" ? "hög" : "high";
    } else if (score >= 2.5) {
      return "neutral";
    } else if (score > 0) {
      return lang === "sv" ? "låg" : "low";
    }
    return "N/A";
  }

  const renderDomain = (results: Results<any>, domain: string)=> {
    const r = results[domain];
    const score = r.avg;
    const domainTemplate = getTemplate(domain)
    let text = "-"
    if (score > 3.5) {
      text = domainTemplate.results[2].text
    } else if (score >= 2.5) {
      text = domainTemplate.results[1].text
    } else if (score > 0) {
      text = domainTemplate.results[0].text
    }

    const facetScores = calculateFacets(domain)

    return (
      <div className="mt-6 bg-indigo-900 rounded-md p-4 border-indigo-800 border" key={"domain_" + domain}>
        <div className="text-lg">
          {domainTemplate.title}
        </div>

        <p className="text-gray-400 mt-2">{text}</p>

        {
          domainTemplate.facets.map((facet, i) => {
            const facetScore = facetScores[facet.facet];
            const facetClass = calculateScoreClass(facetScore);
            const facetValue = (120 * facetScore / 5).toFixed(0) + " (" + facetClass + ")";
            return (
              <div className="mt-4" key={"facet_" + domain + "_" + facet.facet}>
                {i > 0 && <hr className="border-indigo-700"/>}
                <div className="font-medium mt-4">{facet.title}</div>
                <BarValue size={"small"} value={facetScore} min={0} max={5} results={facetValue}
                          className="mt-1"/>
                <div className="mt-4 text-gray-400">
                  {facet.text}
                </div>

              </div>
            )
          })
        }

      </div>
    )
  }

  const calculateResults = () => {
    const results: Results<any> = {
      'A': {count: 0, score: 0, avg: 0},
      'C': {count: 0, score: 0, avg: 0},
      'E': {count: 0, score: 0, avg: 0},
      'N': {count: 0, score: 0, avg: 0},
      'O': {count: 0, score: 0, avg: 0},
    }

    for (let i = 0; i < questions.length; i++) {
      if (answers[i].score > 0) {
        const question: Question = questions[i]
        const r = results[question.domain]
        r.count++
        r.score += answers[i].score
      }
    }

    for (let domain of domains) {
      const r = results[domain]
      r.avg = r.count > 0 ? r.score / r.count : 0
    }

    return results;
  }

  const calculateFacets = (domain: string) => {
    const scores = [0, 0, 0, 0, 0, 0, 0];
    const counts = [0, 0, 0, 0, 0, 0, 0];

    for (let i = 0; i < questions.length; i++) {
      if (answers[i].score > 0) {
        const question: Question = questions[i]
        if (question.domain === domain) {
          counts[question.facet]++;
          scores[question.facet] += answers[i].score;
        }
      }
    }

    for (let i = 0; i < 7; i++) {
      scores[i] = counts[i] > 0 ? scores[i] / counts[i] : 0
    }

    return scores;
  }

  const resetAnswers = () => {
    const _answers: Array<Answer> = [...answers];
    for (let _a of answers) {
      _a.score = 0;
    }
    setAnswers(_answers);
    setConfirmRetake(false);
    setPageNr(1);
    setShowResult(false);
  }

  const renderResult = () => {

    const results = calculateResults();

    return (
      <>
        <div className="flex mt-4">
          <div className="rounded-md bg-indigo-900 p-2 border-indigo-800 border">
            <img src={iconCompass} className="h-10 w-10" alt="compass"/>
          </div>
          <div className="ml-3">
            <div className="z-heading1">
              {lang === "sv" ? "Resultat av personlighetstest" : "Personality assessment result"}
            </div>
            <div className="text-sm text-gray-400">
              {lang === "sv" ? "Skapad" : "Created"}&nbsp;
              {todayStr(created)}
            </div>
          </div>
        </div>

        <div className="mt-6 z-title1">
          {lang === "sv" ? "Din personlighetstyp" : "Your personality profile"}
        </div>

        <div className="mt-4 bg-indigo-900 rounded-md p-4 border-indigo-800 border">
          {
            domains.map(domain => {
              const r = results[domain]
              const domainTemplate = getTemplate(domain)
              const score = (120 * r.avg / 5).toFixed(0) + "";
              return (
                <div key={"header_domain_" + domain}>
                  <div className="font-medium">{domainTemplate.title}</div>
                  <BarValue size={"large"} value={r.avg} min={0} max={5} results={score} className="mt-2 mb-4"/>
                </div>
              )
            })
          }

          <div className="flex justify-between text-sm text-gray-400">
            <div>0</div>
            <div>60</div>
            <div>120</div>
          </div>

          <hr className="border-indigo-700 mt-4"/>

          <div className="mt-4 font-medium">
            {lang === "sv" ? "Analys" : "Analysis"}
          </div>

          <div className="text-gray-400">
            {analysis ? analysis : "Analysis failed - talk to support"}
          </div>

        </div>

        <div className="flex flex-wrap gap-2 mt-6">
          <div style={{width: "calc(33% - 8px)"}}>
            <CustomButton buttonType={selectedDomain === "-" ? "chipBase" : "outlineWhiteBase"}

                          onClick={() => setSelectedDomain("-")}>
              {lang === "sv" ? "Alla" : "All"}
            </CustomButton>
          </div>

          {
            domains.map(domain => {
              const domainTemplate = getTemplate(domain)
              const buttonType = domain === selectedDomain ? "chipBase" : "outlineWhiteBase"
              return (
                <div key={"select_" + domain} style={{width: "calc(33% - 8px)"}}>
                  <CustomButton buttonType={buttonType}
                                onClick={() => setSelectedDomain(domain)}>
                    {domainTemplate.title}
                  </CustomButton>
                </div>
              )
            })
          }
        </div>


        {domains.map(domain => {
          if (selectedDomain === "-" || selectedDomain === domain) {
            return renderDomain(results, domain);
          } else {
            return null;
          }
        })}

        <div className="mt-6 z-title1">
          {lang === "sv" ? "Förstå ditt resultat" : "Understanding your score"}
        </div>

        <div className="mt-4 bg-indigo-900 rounded-md p-4 border-indigo-800 border">
          <div className="font-medium">
            {lang === "sv" ? "Poäng kan vara mellan" : "Score range from"}
          </div>

          <div className="mt-4 text-gray-400 flex justify-between">
            <p>{lang === "sv" ? "Lägsta möjliga poäng" : "Lowest possible score"}</p>
            <p>24</p>
          </div>

          <hr className="mt-2 border-indigo-700"/>

          <div className="mt-2 text-gray-400 flex justify-between">
            <p>{lang === "sv" ? "Högsta möjliga poäng" : "Highest possible score"}</p>
            <p>120</p>
          </div>

          <hr className="mt-2 border-indigo-700"/>

          <div className="mt-4 font-medium">
            {lang === "sv" ? "Om Big Five" : "About the Big Five"}
          </div>

          <div className="text-gray-400">
            {
              lang === "sv" ? "Big Five är en av de mest använda modellerna för att beskriva personlighet. Den består av fem dimensioner: öppenhet, samvetsgrannhet, extraversion, vänlighet och emotionell stabilitet." :
                "The Big Five is one of the most widely used frameworks for describing personality. It consists of five dimensions: Openness, Conscientiousness, Extraversion, Agreeableness, and Neuroticism."
            }
          </div>
          <div className="text-gray-400 mt-2">
            <a className="underline" target="_new" href="https://github.com/rubynor/bigfive-web/blob/master/LICENSE">
              © 2025 — B5 Holding AS - all rights reserved.
            </a>
          </div>
        </div>

        {
          !personalityId &&
            <CustomButton buttonType="outlineWhiteBase" className="mt-4" onClick={() => setConfirmRetake(true)}>
              {lang === "sv" ? "Gör om testet" : "Retake Assessment"}
            </CustomButton>
        }

        {
          confirmRetake &&
            <Modal isOpen={true} onClose={() => setConfirmRetake(false)}>
                <ModalHeader onClose={() => setConfirmRetake(false)}>
                  {lang === "sv" ? "Ta om personlighetstest?" : "Retake personal test?"}
                </ModalHeader>
                <ModalBody>
                    <img src={iconWarning} alt="!" className="h-10 w-10 mx-auto text-center mt-3"/>
                    <p className="mt-3 text-gray-300">
                      {lang === "sv" ? "Om du väljer att ta om personlighetstestet kommer dina nuvarande resultat att " +
                        "raderas och ersättas med de nya." :
                        "If you choose to retake the personality test, your current results will be deleted and replaced " +
                        "with the new ones."}
                    </p>
                </ModalBody>
                <ModalFooter>
                    <div className="w-full">
                        <CustomButton buttonType="outlineWhiteBase" onClick={() => setConfirmRetake(false)}>
                          {lang === "sv" ? "Avbryt" : "Cancel"}
                        </CustomButton>

                        <CustomButton buttonType="blueBase" className="mt-2" onClick={() => resetAnswers()}>
                          {lang === "sv" ? "Ja, ta om testet" : "Yes, retake the test"}
                        </CustomButton>
                    </div>
                </ModalFooter>
            </Modal>
        }
      </>
    )
  }

  const pageName = lang === "sv" ? "Personlighetstest" : "Personality assessment"

  let crumbs;
  if (client) {
    crumbs = [
      {name: lang === "sv" ? "Hem" : "Home", url: "/"},
      {name: t("my-clients"), url: "/clients"},
      {name: client.firstName + " " + client.lastName, url: "/clients/" + client.id},
      {name: pageName},
    ]
  } else {
    crumbs = [
      {name: lang === "sv" ? "Profil" : "Profile", url: "/profile"},
      {name: pageName},
    ]
  }

  if (showResult) {
    return (
      <div className="main-content">
        <LocalBreadcrumbs crumbs={crumbs}/>
        {renderResult()}
      </div>
    )
  }

  return (
    <div className="main-content">
      <LocalBreadcrumbs crumbs={crumbs}/>
      <div className="mt-4">
        <ProgressBar step={pageNr} steps={pages}/>
      </div>
      {
        questions.slice((pageNr - 1) * 3, pageNr * 3).map((question, questionNr) => {
          const _choices = question.keyed === 'plus' ? choices.plus : choices.minus
          const _questionNr = questionNr + (pageNr - 1) * 3
          return (
            <div key={"question_" + _questionNr} className="mt-6">
              <div className="z-title1">
                {question.text}
              </div>
              {
                _choices.map(choice => {
                  const _selectedScore = answers[_questionNr].score
                  return (
                    <div className="mt-2" key={"a_" + _questionNr + '_' + choice.score}>
                      <RadioButton
                        id={"a_" + _questionNr + '_' + choice.score}
                        name={"a_" + _questionNr + '_' + choice.score}
                        label={choice.text}
                        value={"" + choice.score}
                        checked={_selectedScore === choice.score}
                        onChange={() => selectOption(_questionNr, choice.score)} // Set to 1 for "A lot"
                      />
                    </div>
                  )
                })
              }
            </div>
          )
        })
      }

      <div className="flex justify-between mt-6">
        <div className="w-40">
          <CustomButton buttonType="outlineWhiteBase" onClick={() => setPage(-1)}
                        leftIcon={<img src={iconLeft} alt="left"/>} className="w-full">
            {pageNr < 2 ? lang === "sv" ? "Tillbaka till profil" : "Back to Profile" :
              lang === "sv" ? "Föregående" : "Previous"}
          </CustomButton>
        </div>

        <div className="w-40">
          <CustomButton disabled={sending || pageDone()} onClick={() => setPage(1)}
                        rightIcon={<img src={iconRight} alt="right"/>} className="w-full">
            {pages === pageNr ? t('submit') : lang === "sv" ? "Nästa" : "Next"}
          </CustomButton>
        </div>

      </div>
    </div>
  )

}